// assetCache — persistent client-side cache for large binary assets (Gaussian
// splats, animation GLBs) using the Cache Storage API.
//
// Why not rely on the HTTP cache? The convorcher file routes stream assets with
// Transfer-Encoding: chunked and no ETag/Last-Modified, and the body has no
// Content-Length — so browsers frequently refuse to retain them in the HTTP
// disk cache, re-downloading the whole file on every reload. Cache Storage lets
// us persist them explicitly and serve subsequent loads instantly.
//
// Versioning: entries are keyed by the file URL plus a version token (the asset
// row's updatedAt, or a stable id fallback). When the version changes — e.g. a
// re-upload from any client — the stale entry is evicted and the new bytes are
// fetched. This is the "update only when a new version comes" contract; the file
// URL alone is stable per asset and would otherwise mask re-uploads.
//
// Best-effort: caching never blocks or breaks loading. If Cache Storage is
// unavailable or a put() exceeds quota, we warn and fall through to a normal
// download — the real bytes always load (no silent fallback to fake data; this
// is a perf cache over the true server response, per CLAUDE.md rules).
//
// This module is a factory so the splat cache (unbounded, blob-URL consumers)
// and the animation-GLB cache (entry-capped, ArrayBuffer consumers) share one
// implementation. See splatCache.js for the splat instance + named re-exports.

/**
 * @param {object} opts
 * @param {string} opts.cacheName    Cache Storage bucket name (versioned, e.g. 'aos-anim-v1').
 * @param {string} [opts.verParam]   Query param used to encode the version into the cache key.
 * @param {number} [opts.poisonByte] First byte that marks a poisoned entry (default 0x3c = '<', SPA HTML fallback).
 * @param {number} [opts.maxEntries] LRU cap on entry count; oldest-by-insertion evicted on store. Default Infinity.
 * @param {string} [opts.label]      Log prefix.
 * @param {(buf: ArrayBuffer) => string|null} [opts.verify] Format-specific integrity
 *   check for the ArrayBuffer read path, returning a reason string when the entry
 *   is unusable and null when it's fine. A failing entry is evicted and reported
 *   as a miss, so the caller re-fetches. This module stays format-agnostic — the
 *   instance supplies the knowledge (see animCache.js, which passes the GLB
 *   header-length check). Applies to matchArrayBuffer only, NOT matchBlobUrl:
 *   that path exists so a multi-hundred-MB splat is handed to the renderer
 *   without ever being read into memory, and verifying it would defeat the point.
 * @param {(blob: Blob) => Promise<string|null>|string|null} [opts.verifyBlob]
 *   The matchBlobUrl-path sibling of `verify`, with the constraint that path
 *   exists to honor: it receives the Blob, NOT the bytes, so an instance can
 *   check metadata (blob.size) or slice a small header without materialising
 *   a multi-hundred-MB body. A failing entry is evicted and reported as a
 *   miss (see splatCache.js, which checks a PLY header's declared size against
 *   blob.size — the truncated-download case that replayed forever otherwise).
 */
export function createAssetCache({
  cacheName,
  verParam = '__splatver',
  poisonByte = 0x3c,
  maxEntries = Infinity,
  label = cacheName,
  verify = null,
  verifyBlob = null,
}) {
  const supported = () => typeof caches !== 'undefined' && typeof window !== 'undefined'

  /** Synthetic cache key: the real URL plus a version query we never actually fetch. */
  function keyFor(url, version) {
    const sep = url.includes('?') ? '&' : '?'
    return `${url}${sep}${verParam}=${encodeURIComponent(version ?? '0')}`
  }

  /** Strip the version param so we can group all cached versions of one URL. */
  function baseOf(key) {
    return key.replace(new RegExp(`([?&])${verParam}=[^&]*`), '').replace(/[?&]$/, '')
  }

  /** True if the blob's first byte marks a poisoned (HTML-fallback) entry. */
  async function isPoisoned(blob) {
    const head = new Uint8Array(await blob.slice(0, 1).arrayBuffer())
    return head[0] === poisonByte
  }

  /**
   * Return a `blob:` object URL for the cached asset if present at this version,
   * else null. Caller owns the returned URL and must revokeObjectURL it.
   */
  async function matchBlobUrl(url, version) {
    if (!supported()) return null
    try {
      const cache = await caches.open(cacheName)
      const hit = await cache.match(keyFor(url, version))
      if (!hit) return null
      const blob = await hit.blob()
      // Self-heal a poisoned entry: an earlier build cached the SPA-fallback
      // HTML for a missing file. '<' is never a valid binary header — evict it
      // and report a miss so the caller re-fetches (and now fails loudly).
      if (await isPoisoned(blob)) {
        console.warn(`[${label}] cached entry is HTML, not a binary — evicting`, url)
        await cache.delete(keyFor(url, version))
        return null
      }
      // Format-specific blob check (metadata / header slice only — never the
      // whole body). Same trust-but-verify rule as matchArrayBuffer's `verify`:
      // this bucket is cache-first, so an entry written by a download that
      // ended early replays on every later load unless a read evicts it.
      if (verifyBlob) {
        const bad = await verifyBlob(blob)
        if (bad) {
          console.warn(`[${label}] evicting unusable cache entry (${bad}) — refetching`, url)
          await cache.delete(keyFor(url, version))
          return null
        }
      }
      return URL.createObjectURL(blob)
    } catch (err) {
      console.warn(`[${label}] match failed:`, err)
      return null
    }
  }

  /**
   * Why a cached ArrayBuffer must not be served, or null when it's fine. Three
   * shapes of poison, cheapest first: a 0-byte body, the SPA HTML fallback, and
   * whatever the instance's own `verify` rejects (a truncated GLB, for animCache).
   *
   * Trust, but verify: this bucket is CACHE-FIRST, so an entry written before a
   * given check existed — or by a download that ended early — is replayed on
   * every later load, and no reload and no deploy can dislodge it. Checking on
   * read is what lets an already-poisoned cache self-heal.
   */
  function reasonUnusable(buf) {
    if (buf.byteLength === 0) return 'empty (0 bytes)'
    if (new Uint8Array(buf, 0, 1)[0] === poisonByte) return 'HTML, not a binary'
    return verify ? verify(buf) : null
  }

  /**
   * Return the cached bytes as an ArrayBuffer if present at this version, else
   * null. Lets a loader parse directly (e.g. GLTFLoader.parse) without a blob
   * URL round-trip.
   */
  async function matchArrayBuffer(url, version) {
    if (!supported()) return null
    try {
      const cache = await caches.open(cacheName)
      const hit = await cache.match(keyFor(url, version))
      if (!hit) return null
      // One read of the body, then every check runs off those bytes — the blob
      // is fully materialised on this path regardless.
      const buf = await hit.arrayBuffer()
      const bad = reasonUnusable(buf)
      if (bad) {
        console.warn(`[${label}] evicting unusable cache entry (${bad}) — refetching`, url)
        await cache.delete(keyFor(url, version))
        return null
      }
      return buf
    } catch (err) {
      console.warn(`[${label}] match failed:`, err)
      return null
    }
  }

  /**
   * Store a freshly-downloaded blob under (url, version), first evicting any
   * stale versions of the same URL so we don't accumulate old copies, then
   * trimming the bucket to maxEntries (oldest-by-insertion first).
   */
  async function store(url, version, blob) {
    if (!supported()) return
    try {
      const cache = await caches.open(cacheName)
      const base = baseOf(keyFor(url, version))
      for (const req of await cache.keys()) {
        if (baseOf(req.url) === base) await cache.delete(req)
      }
      await cache.put(keyFor(url, version), new Response(blob))
      // LRU trim: Cache Storage keys() returns insertion order, so the entry we
      // just put is last — evict from the front until under the cap. O(n) on the
      // cold store path only; reads never pay this.
      if (Number.isFinite(maxEntries)) {
        let keys = await cache.keys()
        let i = 0
        while (keys.length - i > maxEntries) {
          await cache.delete(keys[i])
          i++
        }
      }
      console.log(`[${label}] stored ${(blob.size / 1e6).toFixed(1)}MB for ${url} (v=${version ?? '0'})`)
    } catch (err) {
      // QuotaExceededError etc. — perf cache only, so warn and move on.
      console.warn(`[${label}] store failed (continuing uncached):`, err)
    }
  }

  /** Drop every cached version of a URL (e.g. on local re-upload/delete). */
  async function evict(url) {
    if (!supported()) return
    try {
      const cache = await caches.open(cacheName)
      const base = baseOf(keyFor(url, '0'))
      for (const req of await cache.keys()) {
        if (baseOf(req.url) === base) await cache.delete(req)
      }
    } catch (err) {
      console.warn(`[${label}] evict failed:`, err)
    }
  }

  /** Nuke the whole cache bucket. */
  async function clearAll() {
    if (!supported()) return
    try { await caches.delete(cacheName) } catch (err) { console.warn(`[${label}] clearAll failed:`, err) }
  }

  return { keyFor, baseOf, matchBlobUrl, matchArrayBuffer, store, evict, clearAll, CACHE_NAME: cacheName }
}
